import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Package, 
  CreditCard, 
  MapPin, 
  Copy,
  Clock,
  Phone,
  Mail,
  Leaf,
  Wine,
  Calendar,
  Euro,
  Download,
  Image as ImageIcon
} from 'lucide-react';
import { ordersAPI } from '../services/api';
import Header from './Header';
import Footer from './Footer';

const OrderDetails = () => {
  const { orderNumber } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadOrder = async () => {
      try {
        setLoading(true);
        setError(null);
        
        if (!orderNumber) {
          setError('Numero ordine non specificato');
          return;
        }

        const response = await ordersAPI.getOrderByNumber(orderNumber);
        
        if (response.success && response.order) {
          setOrder(response.order);
        } else {
          setError(response.message || 'Ordine non trovato');
        }
      } catch (err) {
        console.error('Error loading order:', err);
        setError('Errore nel caricamento dell\'ordine');
      } finally {
        setLoading(false);
      }
    };

    loadOrder();
  }, [orderNumber]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      // Could add a toast notification here
      console.log('Copied to clipboard:', text);
    });
  };

  const getPaymentMethodDisplay = (method) => {
    const paymentMethods = {
      'bank_transfer': 'Bonifico Bancario',
      'credit_card': 'Carta di Credito',
      'stripe': 'Carta di Credito (Stripe)',
      'paypal': 'PayPal'
    };
    return paymentMethods[method] || method || 'Non specificato';
  };

  const getPaymentMethodIcon = (method) => {
    switch (method) {
      case 'bank_transfer':
        return <Euro className="w-5 h-5 text-green-600" />;
      case 'credit_card':
      case 'stripe':
        return <CreditCard className="w-5 h-5 text-blue-600" />;
      case 'paypal':
        return <Package className="w-5 h-5 text-yellow-600" />;
      default:
        return <CreditCard className="w-5 h-5 text-gray-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'In attesa di pagamento':
      case 'pending_payment':
        return 'text-yellow-600 bg-yellow-100';
      case 'Pagamento confermato':
      case 'payment_confirmed':
        return 'text-green-600 bg-green-100';
      case 'In lavorazione':
      case 'in_production':
        return 'text-blue-600 bg-blue-100';
      case 'Spedito':
      case 'shipped':
        return 'text-purple-600 bg-purple-100';
      case 'Consegnato':
      case 'delivered':
        return 'text-green-600 bg-green-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('it-IT', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
            <span className="ml-4 text-gray-600">Caricamento dettagli ordine...</span>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Ordine non trovato</h1>
            <p className="text-gray-600 mb-8">
              {error || 'L\'ordine richiesto non esiste o non hai i permessi per visualizzarlo.'}
            </p>
            <Link
              to="/dashboard"
              className="inline-flex items-center px-6 py-3 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Torna alla Dashboard
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header with Back Button */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center">
            <Link
              to="/dashboard"
              className="inline-flex items-center text-purple-600 hover:text-purple-700 mr-4"
            >
              <ArrowLeft className="w-5 h-5 mr-1" />
              Dashboard
            </Link>
            <h1 className="text-2xl font-bold text-gray-900">Dettagli Ordine</h1>
          </div>
        </div>

        {/* Order Summary Card */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="grid md:grid-cols-3 gap-6">
            {/* Order Info */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Informazioni Ordine</h2>
              <div className="space-y-2 text-sm">
                <p><span className="text-gray-600">Numero:</span> <span className="font-medium">{order.orderNumber}</span></p>
                <p><span className="text-gray-600">Data:</span> <span className="font-medium">{formatDate(order.createdAt)}</span></p>
                <div className="flex items-center">
                  <span className="text-gray-600 mr-2">Stato:</span>
                  <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                    {order.status}
                  </span>
                </div>
                
                {/* Tracking Number - B2B */}
                {order.shippingInfo?.trackingNumber && (
                  <div className="pt-2 border-t border-gray-200">
                    <div className="flex items-center space-x-2">
                      <Package className="w-4 h-4 text-purple-600" />
                      <span className="text-gray-600">Tracking:</span>
                      <span className="font-mono font-semibold text-purple-600">{order.shippingInfo.trackingNumber}</span>
                      <button
                        onClick={() => copyToClipboard(order.shippingInfo.trackingNumber)}
                        className="p-1 hover:bg-gray-100 rounded transition-colors"
                        title="Copia tracking"
                      >
                        <Copy className="w-4 h-4 text-gray-400" />
                      </button>
                    </div>
                    {order.shippingInfo?.carrier && (
                      <p className="text-xs text-gray-500 mt-1 ml-6">Corriere: {order.shippingInfo.carrier}</p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Payment Info */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Pagamento</h2>
              <div className="space-y-2 text-sm">
                <div className="flex items-center">
                  {getPaymentMethodIcon(order.paymentInfo?.method)}
                  <span className="ml-2 font-medium">{getPaymentMethodDisplay(order.paymentInfo?.method)}</span>
                </div>
                <p><span className="text-gray-600">Subtotale:</span> <span className="font-medium">€{order.subtotal?.toFixed(2)}</span></p>
                <p><span className="text-gray-600">Spedizione:</span> <span className="font-medium">€{order.shippingCost?.toFixed(2)}</span></p>
                <p><span className="text-gray-600">IVA:</span> <span className="font-medium">€{order.taxAmount?.toFixed(2)}</span></p>
                <div className="border-t pt-2">
                  <p className="font-bold text-lg text-purple-600">Totale: €{order.totalAmount?.toFixed(2)}</p>
                </div>
              </div>
            </div>

            {/* Contact */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Contatto</h2>
              <div className="space-y-2 text-sm">
                <div className="flex items-center">
                  <Mail className="w-4 h-4 text-gray-400 mr-2" />
                  <span className="text-gray-600">{order.userEmail}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Products */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Prodotti Ordinati</h2>
          <div className="space-y-4">
            {order.items?.map((item, index) => {
              const productName = item.name || item.liquorType || 'Liquore Personalizzato';
              const selectedIngredients = item.selectedIngredients || [];
              const bottleType = item.bottleType;

              return (
                <div key={index} className="border rounded-lg p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">{productName}</h3>
                      {item.description && (
                        <p className="text-sm text-gray-600 mt-1">{item.description}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-600">Quantità: {item.quantity}</p>
                      <p className="font-semibold">€{item.totalPrice?.toFixed(2)}</p>
                    </div>
                  </div>

                  {/* Product Details */}
                  <div className="grid md:grid-cols-2 gap-4 text-sm">
                    {/* Ingredients */}
                    {selectedIngredients && selectedIngredients.length > 0 && (
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                          <Leaf className="w-4 h-4 mr-1 text-green-600" />
                          Ingredienti Selezionati
                        </h4>
                        <div className="space-y-1">
                          {selectedIngredients.map((ingredient, idx) => (
                            <div key={idx} className="flex justify-between text-xs bg-gray-50 p-2 rounded">
                              <span>{ingredient.name}</span>
                              <span className="text-gray-600">{ingredient.quantity}g</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Bottle Info */}
                    {bottleType && (
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                          <Wine className="w-4 h-4 mr-1 text-purple-600" />
                          Bottiglia
                        </h4>
                        <div className="text-xs bg-gray-50 p-2 rounded">
                          <p className="font-medium">{bottleType.name || bottleType}</p>
                          {bottleType.description && (
                            <p className="text-gray-600 mt-1">{bottleType.description}</p>
                          )}
                          {bottleType.capacity && (
                            <p className="text-gray-600">Capacità: {bottleType.capacity}ml</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Additional Info */}
                  <div className="mt-3 pt-3 border-t text-xs text-gray-600 space-y-1">
                    {item.aromaticIntensity && (
                      <p>Intensità aromatica: <span className="font-medium">{item.aromaticIntensity}</span></p>
                    )}
                    {item.labelText && (
                      <p>Testo etichetta: <span className="font-medium">"{item.labelText}"</span></p>
                    )}
                    {item.labelColor && (
                      <p>Colore etichetta: <span className="font-medium">{item.labelColor}</span></p>
                    )}
                  </div>

                  {/* Label Files - Etichetta e Anteprima 3D */}
                  {(item.labelImage || item.bottlePreview) && (
                    <div className="mt-4 pt-4 border-t">
                      <h4 className="font-medium text-gray-900 mb-3 flex items-center text-sm">
                        <ImageIcon className="w-4 h-4 mr-1 text-blue-600" />
                        File Etichetta
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Helper function to get full URL */}
                        {(() => {
                          const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
                          const getImageUrl = (path) => {
                            if (!path) return null;
                            if (path.startsWith('http')) return path;
                            return `${backendUrl}/api/upload/serve/${path}`;
                          };

                          return (
                            <>
                              {/* Etichetta Originale */}
                              {item.labelImage && (
                                <div className="bg-gray-50 rounded-lg p-4">
                                  <p className="text-xs font-medium text-gray-700 mb-2">Etichetta Originale</p>
                                  <div className="relative group">
                                    <img 
                                      src={getImageUrl(item.labelImage)} 
                                      alt="Etichetta cliente"
                                      className="w-full h-32 object-contain rounded border border-gray-200 bg-white"
                                    />
                                    <a
                                      href={getImageUrl(item.labelImage)}
                                      download
                                      className="absolute top-2 right-2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg transition-all opacity-0 group-hover:opacity-100"
                                    >
                                      <Download className="w-4 h-4 text-gray-700" />
                                    </a>
                                  </div>
                                </div>
                              )}

                              {/* Anteprima 3D */}
                              {item.bottlePreview && (
                                <div className="bg-gray-50 rounded-lg p-4">
                                  <p className="text-xs font-medium text-gray-700 mb-2">Anteprima 3D Bottiglia</p>
                                  <div className="relative group">
                                    <img 
                                      src={getImageUrl(item.bottlePreview)} 
                                      alt="Anteprima 3D"
                                      className="w-full h-32 object-contain rounded border border-gray-200 bg-white"
                                    />
                                    <a
                                      href={getImageUrl(item.bottlePreview)}
                                      download
                                      className="absolute top-2 right-2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg transition-all opacity-0 group-hover:opacity-100"
                                    >
                                      <Download className="w-4 h-4 text-gray-700" />
                                    </a>
                                  </div>
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Addresses */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Shipping Address */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <MapPin className="w-5 h-5 mr-2 text-blue-600" />
              Indirizzo di Spedizione
            </h2>
            {order.shippingAddress ? (
              <div className="space-y-2 text-sm">
                <p className="font-medium">{order.shippingAddress.firstName} {order.shippingAddress.lastName}</p>
                {order.shippingAddress.company && <p>{order.shippingAddress.company}</p>}
                <p>{order.shippingAddress.street} {order.shippingAddress.streetNumber}</p>
                <p>{order.shippingAddress.zipCode} {order.shippingAddress.city} ({order.shippingAddress.province})</p>
                {order.shippingAddress.phone && (
                  <div className="flex items-center">
                    <Phone className="w-4 h-4 text-gray-400 mr-2" />
                    <span>{order.shippingAddress.phone}</span>
                  </div>
                )}
                {order.shippingAddress.notes && (
                  <p className="text-gray-600 italic mt-2">Note: {order.shippingAddress.notes}</p>
                )}
              </div>
            ) : (
              <p className="text-gray-500 italic">Indirizzo di spedizione non disponibile</p>
            )}
          </div>

          {/* Billing Address */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <CreditCard className="w-5 h-5 mr-2 text-purple-600" />
              Indirizzo di Fatturazione
            </h2>
            {order.billingAddress ? (
              <div className="space-y-2 text-sm">
                <p className="font-medium">{order.billingAddress.firstName} {order.billingAddress.lastName}</p>
                {order.billingAddress.company && <p>{order.billingAddress.company}</p>}
                <p>{order.billingAddress.street} {order.billingAddress.streetNumber}</p>
                <p>{order.billingAddress.zipCode} {order.billingAddress.city} ({order.billingAddress.province})</p>
                {order.billingAddress.phone && (
                  <div className="flex items-center">
                    <Phone className="w-4 h-4 text-gray-400 mr-2" />
                    <span>{order.billingAddress.phone}</span>
                  </div>
                )}
                {order.billingAddress.notes && (
                  <p className="text-gray-600 italic mt-2">Note: {order.billingAddress.notes}</p>
                )}
              </div>
            ) : (
              <p className="text-gray-500 italic">Uguale all'indirizzo di spedizione</p>
            )}
          </div>
        </div>

        {/* Payment Instructions (for bank transfer) */}
        {order.paymentInfo?.method === 'bank_transfer' && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 mt-6">
            <h2 className="text-lg font-semibold text-amber-900 mb-4">Istruzioni per il Bonifico Bancario</h2>
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-amber-800 mb-2"><strong>Beneficiario:</strong></p>
                <div className="flex justify-between items-center bg-white p-3 rounded border">
                  <p className="text-amber-900 flex-1">Lacinio SRL</p>
                  <button
                    onClick={() => copyToClipboard("Lacinio SRL")}
                    className="ml-2 p-1 text-amber-600 hover:text-amber-800"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div>
                <p className="text-amber-800 mb-2"><strong>IBAN:</strong></p>
                <div className="flex justify-between items-center bg-white p-3 rounded border">
                  <p className="text-amber-900 flex-1">IT84T0200822201000105841983</p>
                  <button
                    onClick={() => copyToClipboard("IT84T0200822201000105841983")}
                    className="ml-2 p-1 text-amber-600 hover:text-amber-800"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div>
                <p className="text-amber-800 mb-2"><strong>Causale:</strong></p>
                <div className="flex justify-between items-center bg-white p-3 rounded border">
                  <p className="text-amber-900 flex-1">{order.orderNumber}</p>
                  <button
                    onClick={() => copyToClipboard(order.orderNumber)}
                    className="ml-2 p-1 text-amber-600 hover:text-amber-800"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div>
                <p className="text-amber-800 mb-2"><strong>Importo:</strong></p>
                <div className="bg-white p-3 rounded border">
                  <p className="text-amber-900 font-semibold">€{order.totalAmount?.toFixed(2)}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      
      <Footer />
    </div>
  );
};

export default OrderDetails;